import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import '../styles/Pomodoro.css';
import ColorWheel from '../components/ColorWheel';
import { startNoise, stopNoise, setNoiseVolume, playStartChime, playEndChime, playBreakChime } from '../utils/soundUtils';
import { showOSNotification } from '../utils/notify';
import NotesEditor from '../components/NotesEditor';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 番茄到点后没人管的话：每 5 分钟提醒一次，第 4 次（+20 分钟）直接替用户结束。
// 不设上限就会出现「一个番茄跑了几百小时」。
const OVERTIME_STEP_MIN = 5;
const OVERTIME_MAX_MARKS = 4;

const STATUS_CYCLE = { todo: 'in-progress', 'in-progress': 'done', done: 'todo' };
const STATUS_ICON  = { todo: '○', 'in-progress': '⏵', done: '✓' };

// index === Date#getDay() (0 = Sunday), so a day list can be used directly
// against `new Date().getDay()` with no remapping.
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

// Extra dropdown-only project labels — shown alongside 未分类 in the
// manual-entry + edit-session project pickers. Not real projects on the
// board; picking one just tags the session with that title + color.
const EXTRA_PROJECT_OPTIONS = [
  { title: '听课', color: '#fbcfe8' },
  { title: '报告', color: '#e2d5f5' },
];

// 未分类番茄/会话的显示色——不能落到 PROJECT_COLORS 里的任何一个，那样看着
// 就像是在借用某个真实项目的颜色，用户已经反馈过这个观感。
const UNCATEGORIZED_COLOR = '#9e9e9e';

const PROJECT_COLORS = [
  '#64ffda','#00bfa5','#1de9b6','#69f0ae','#00e676',
  '#40c4ff','#448aff','#82b1ff','#7c4dff','#651fff',
  '#e040fb','#ff4081','#ff6b9d','#ff6e40','#ffab40',
  '#ffd740','#ffd166','#f4ff81','#ccff90','#a7ffeb',
  '#84ffff','#b9f6ca','#ff80ab','#b3e5fc',
  // Extra-pale pastels used for 听课 / 报告
  '#fbcfe8', // pale pink
  '#e2d5f5', // pale lavender
];

const DEFAULT_SETTINGS = {
  workDuration: 30, shortBreak: 5, longBreak: 15, autoRest: false, autoStop: true,
  noiseType: 'none', noiseVolume: 0.5, alertSounds: true,
};

const SOUND_OPTIONS = [
  { value: 'none',     label: '关闭' },
  { value: 'ocean',    label: '🌊 海浪' },
  { value: 'campfire', label: '🔥 篝火' },
  { value: 'cafe',     label: '☕ 咖啡厅' },
  { value: 'forest',   label: '🌲 森林' },
];

// Daily rest-break checklist. Shown only in rest mode.
// - `daily` tasks reset every calendar day (localStorage key includes the date)
// - `cooldownDays` tasks lock for N days after each tap (e.g. watering plants)
// Emoji-only, small buttons; multiple water cups so each glass gets its own tap.
const REST_TASKS = [
  { id: 'water-1',   emoji: '🥛', title: '喝水 1/6' },
  { id: 'water-2',   emoji: '🥛', title: '喝水 2/6' },
  { id: 'water-3',   emoji: '🥛', title: '喝水 3/6' },
  { id: 'water-4',   emoji: '🥛', title: '喝水 4/6' },
  { id: 'water-5',   emoji: '🥛', title: '喝水 5/6' },
  { id: 'water-6',   emoji: '🥛', title: '喝水 6/6' },
  { id: 'strength',  emoji: '🦍', title: '力量' },
  { id: 'legs',      emoji: '🦵', title: '腿部' },
  { id: 'stretch',   emoji: '🤸', title: '拉伸' },
  { id: 'meditate',  emoji: '🧘', title: '冥想' },
  { id: 'back',      emoji: '💪', title: '练背' },
  { id: 'breathe',   emoji: '🌬️', title: '呼吸' },
  { id: 'supplement',emoji: '💊', title: '补剂 / 吃药' },
  { id: 'checkin',   emoji: '🔘', title: '打卡' },
  { id: 'eyes',      emoji: '👀', title: '眼睛放松' },
  { id: 'plant',     emoji: '🌿💦', title: '给绿植浇水', cooldownDays: 5 },
];

// Rest-task state is server-backed (see /api/pomo/rest-tasks) so every device
// sees the same set. Shape: { daily: { "YYYY-MM-DD": [id, ...] }, cooldown: { id: lastTs } }
const restTodayKey = () => new Date().toISOString().slice(0, 10);

// One-time migration: pull any old localStorage state into the server payload
// so existing per-device data isn't lost the first time we sync.
function mergeLocalIntoRestState(server) {
  const merged = {
    daily: { ...(server?.daily || {}) },
    cooldown: { ...(server?.cooldown || {}) },
  };
  let changed = false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('rest-tasks-')) {
        const date = k.slice('rest-tasks-'.length);
        try {
          const list = JSON.parse(localStorage.getItem(k) || '[]');
          if (Array.isArray(list) && list.length > 0) {
            const cur = new Set(merged.daily[date] || []);
            list.forEach(id => cur.add(id));
            merged.daily[date] = [...cur];
            changed = true;
          }
        } catch {}
      } else if (k.startsWith('rest-task-last-')) {
        const id = k.slice('rest-task-last-'.length);
        const val = parseInt(localStorage.getItem(k) || '0', 10);
        if (val > 0 && val > (merged.cooldown[id] || 0)) {
          merged.cooldown[id] = val;
          changed = true;
        }
      }
    }
  } catch {}
  return { merged, changed };
}

function clearLocalRestTaskKeys() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('rest-tasks-') || k.startsWith('rest-task-last-'))) {
        toRemove.push(k);
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch {}
}

function loadSettings() {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem('pomo-settings') || '{}') }; }
  catch { return DEFAULT_SETTINGS; }
}

// ── Date range helpers ─────────────────────────────────────

const RANGES = [
  { key: 'today', label: '今天' },
  { key: 'week',  label: '近7天' },
  { key: 'month', label: '近30天' },
  { key: 'custom',label: '自定义' },
];

function getRangeBounds(range, customStart, customEnd) {
  const now = new Date();
  const end = new Date(now); end.setHours(23, 59, 59, 999);
  if (range === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (range === 'week') {
    const start = new Date(now - 6 * 864e5); start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (range === 'month') {
    const start = new Date(now - 29 * 864e5); start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (customStart && customEnd) {
    return {
      start: new Date(customStart + 'T00:00:00'),
      end:   new Date(customEnd   + 'T23:59:59'),
    };
  }
  return { start: new Date(0), end };
}

function fmtDay(iso) {
  const d = new Date(iso);
  return `${d.getMonth()+1}/${d.getDate()}`;
}

// ── Stats panel ────────────────────────────────────────────

const TOOLTIP_STYLE = {
  contentStyle: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', fontSize: '0.78rem' },
  labelStyle:   { color: '#aaa' },
  itemStyle:    { color: '#e8e8e8' },
  cursor:       { fill: 'rgba(255,255,255,0.04)' },
};

function StatsPanel({ sessions, projects }) {
  const [range, setRange]           = useState('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [showManual,  setShowManual]  = useState(false);

  // Manual form state
  const today = new Date().toISOString().slice(0, 10);
  const [mDate,    setMDate]    = useState(today);
  const [mTime,    setMTime]    = useState('09:00');
  const [mDur,     setMDur]     = useState(30);
  const [mProj,    setMProj]    = useState('');
  const [mNotes,   setMNotes]   = useState('');
  const [mSaving,  setMSaving]  = useState(false);

  const { start, end } = getRangeBounds(range, customStart, customEnd);

  const filtered = useMemo(() =>
    sessions.filter(s => { const t = new Date(s.startTime); return t >= start && t <= end; }),
    [sessions, start, end] // eslint-disable-line
  );

  const totalMin  = filtered.reduce((s, r) => s + r.duration, 0);
  const totalSess = filtered.length;
  const days      = Math.max(1, Math.ceil((end - start) / 864e5));
  const avgMin    = Math.round(totalMin / days);

  // Daily bar chart
  const dailyData = useMemo(() => {
    const map = {};
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      map[fmtDay(d.toISOString())] = 0;
    }
    filtered.forEach(s => {
      const k = fmtDay(s.startTime);
      if (k in map) map[k] += s.duration;
    });
    return Object.entries(map).map(([date, minutes]) => ({ date, minutes }));
  }, [filtered]); // eslint-disable-line

  // Project pie — iterate each session's per-project breakdown (new format)
  // and fall back to (projectTitle, duration) for legacy single-project rows.
  const projectData = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      const projs = Array.isArray(s.projects) && s.projects.length > 0
        ? s.projects
        : [{ title: s.projectTitle, color: s.projectColor, minutes: s.duration }];
      projs.forEach(p => {
        const key = p.title || '未分类';
        if (!map[key]) map[key] = { name: key, value: 0, color: p.color || UNCATEGORIZED_COLOR };
        map[key].value += p.minutes || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Task ranking
  const taskData = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      const k = `${s.projectTitle}:::${s.taskTitle}`;
      if (!map[k]) map[k] = { taskTitle: s.taskTitle, projectTitle: s.projectTitle, color: s.projectColor || UNCATEGORIZED_COLOR, minutes: 0, count: 0 };
      map[k].minutes += s.duration;
      map[k].count++;
    });
    return Object.values(map).sort((a, b) => b.minutes - a.minutes).slice(0, 6);
  }, [filtered]);

  const submitManual = async (e) => {
    e.preventDefault();
    setMSaving(true);
    const proj = projects.find(p => p.title === mProj)
      || EXTRA_PROJECT_OPTIONS.find(p => p.title === mProj);
    const startTime = new Date(`${mDate}T${mTime}:00`).toISOString();
    const endTime   = new Date(new Date(startTime).getTime() + mDur * 60000).toISOString();
    await axios.post(`${API}/pomo/sessions`, {
      projectTitle: mProj || '未分类',
      projectColor: proj?.color || null, startTime, endTime,
      duration: mDur, notes: mNotes,
    }, { headers: authHeaders() });
    setMSaving(false);
    setShowManual(false);
    setMNotes('');
    // trigger reload in parent via window event
    window.dispatchEvent(new Event('pomo-reload'));
  };

  return (
    <div className="pomo-stats">
      {/* Manual modal */}
      {showManual && (
        <div className="pomo-modal-overlay" onClick={() => setShowManual(false)}>
          <div className="pomo-modal" onClick={e => e.stopPropagation()}>
            <h3>补记番茄</h3>
            <form onSubmit={submitManual} className="pomo-manual-form">
              <div className="pomo-manual-row">
                <label>日期</label>
                <input type="date" value={mDate} onChange={e => setMDate(e.target.value)} required />
              </div>
              <div className="pomo-manual-row">
                <label>开始时间</label>
                <input type="time" value={mTime} onChange={e => setMTime(e.target.value)} required />
              </div>
              <div className="pomo-manual-row">
                <label>时长 (分钟)</label>
                <input type="number" min="1" max="300" value={mDur} onChange={e => setMDur(+e.target.value)} required />
              </div>
              <div className="pomo-manual-row">
                <label>项目</label>
                <select value={mProj} onChange={e => setMProj(e.target.value)}>
                  <option value="">未分类</option>
                  {EXTRA_PROJECT_OPTIONS.map(opt => (
                    <option key={opt.title} value={opt.title}>{opt.title}</option>
                  ))}
                  {projects.map(p => <option key={p.id} value={p.title}>{p.title}</option>)}
                </select>
              </div>
              <div className="pomo-manual-row">
                <label>备注</label>
                <input type="text" value={mNotes} onChange={e => setMNotes(e.target.value)} placeholder="备注（可选）" />
              </div>
              <div className="pomo-modal-actions">
                <button type="submit" className="btn btn-primary btn-small" disabled={mSaving}>{mSaving ? '保存…' : '保存'}</button>
                <button type="button" className="btn btn-small" onClick={() => setShowManual(false)}>取消</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="pomo-stats-header">
        <span className="pomo-stats-title">统计</span>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button className="btn btn-small" onClick={async () => {
            const res = await axios.get(`${API}/export`, { headers: authHeaders() });
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `backup-${new Date().toISOString().slice(0,10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}>↓ 导出</button>
          <button className="btn btn-small" onClick={() => {
            // Default the start time to "now − 30 min" — a just-finished
            // pomo is by far the most common case for manual entry.
            const past = new Date(Date.now() - 30 * 60000);
            const pad = n => String(n).padStart(2, '0');
            setMDate(`${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())}`);
            setMTime(`${pad(past.getHours())}:${pad(past.getMinutes())}`);
            setMDur(30);
            setShowManual(true);
          }}>＋ 补记</button>
        </div>
      </div>

      {/* Range selector */}
      <div className="pomo-range-btns">
        {RANGES.map(r => (
          <button key={r.key} className={`pomo-range-btn${range === r.key ? ' active' : ''}`}
            onClick={() => setRange(r.key)}>{r.label}</button>
        ))}
      </div>
      {range === 'custom' && (
        <div className="pomo-custom-range">
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
          <span>—</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
        </div>
      )}

      {/* Summary */}
      <div className="pomo-stats-summary">
        <div className="pomo-stat-item"><span className="pomo-stat-num">{totalSess}</span><span className="pomo-stat-label">番茄</span></div>
        <div className="pomo-stat-item"><span className="pomo-stat-num">{(totalMin/60).toFixed(1)}</span><span className="pomo-stat-label">小时</span></div>
        <div className="pomo-stat-item"><span className="pomo-stat-num">{avgMin}</span><span className="pomo-stat-label">分/天均</span></div>
      </div>

      {totalSess === 0 ? (
        <div className="pomo-stats-empty">这段时间没有记录</div>
      ) : (
        <>
          {/* Daily bar chart */}
          <div className="pomo-chart-label">每日专注时长（分钟）</div>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={dailyData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#666', fontSize: 9 }} />
              <Tooltip {...TOOLTIP_STYLE} formatter={v => [`${v} 分钟`]} />
              <Bar dataKey="minutes" fill="#64ffda" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Project pie */}
          {projectData.length > 0 && (
            <>
              <div className="pomo-chart-label">项目分布</div>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={projectData} dataKey="value" innerRadius={38} outerRadius={60}
                    paddingAngle={2}>
                    {projectData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip {...TOOLTIP_STYLE} formatter={v => [`${v} 分钟`]} />
                  <Legend
                    formatter={(value, entry) => (
                      <span style={{ color: '#aaa', fontSize: '0.72rem' }}>
                        {value} {Math.round(entry.payload.value)}m
                      </span>
                    )}
                    iconSize={8}
                    wrapperStyle={{ fontSize: '0.72rem' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </>
          )}

        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────

export default function Pomodoro() {
  const [loggedIn, setLoggedIn]     = useState(!!localStorage.getItem('token'));
  const [password, setPassword]     = useState('');
  const [loginError, setLoginError] = useState('');

  const [projects, setProjects] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectColor, setNewProjectColor] = useState(PROJECT_COLORS[0]);
  const [colorPickerFor, setColorPickerFor] = useState(null);   // 正在改色的项目 id
  const [showNewProject, setShowNewProject]   = useState(false);
  const [newTaskInputs, setNewTaskInputs]     = useState({});

  // 双击项目卡打开，配置这个项目的周期性任务（比如"每周一"）——只在匹配的
  // 那天才会真的出现在任务列表里，见下面 materializeRecurringTasks。
  const [recurringModalFor, setRecurringModalFor] = useState(null);   // 正在编辑的项目 id
  const [newRecurTitle, setNewRecurTitle]         = useState('');
  const [newRecurDays, setNewRecurDays]           = useState([]);     // [0-6]，0=周日

  const [editingSessionFull, setEditingSessionFull] = useState(null);
  const [editSessionData, setEditSessionData]       = useState({});
  const [sessionTasks, setSessionTasks]             = useState([]);

  // Modal overlays close on click-outside. A plain onClick is wrong for
  // this: selecting text inside the modal (e.g. the notes textarea) can
  // easily drag the mouse-up past the modal's edge, and a native `click`
  // fires whichever element the mouse-up landed on — silently closing the
  // modal mid-edit ("手一滑卡片就消失"). Only close when BOTH the press and
  // the release started on the overlay itself, not somewhere inside it.
  const overlayPressedSelf = useRef(false);
  const overlayCloseProps = (onClose) => ({
    onMouseDown: e => { overlayPressedSelf.current = e.target === e.currentTarget; },
    onClick: e => { if (overlayPressedSelf.current && e.target === e.currentTarget) onClose(); },
  });

  // Load settings ONCE and use it for every initial value so we don't start
  // with the hard-coded DEFAULT_SETTINGS.workDuration on the display when the
  // user has a different saved value.
  const initialSettings = useMemo(loadSettings, []);

  const [timerState, setTimerState]     = useState({ running: false });
  const [timerMode, setTimerMode]       = useState('work');
  const [timeLeft, setTimeLeft]         = useState(initialSettings.workDuration * 60);
  const [pendingTask, setPendingTask]   = useState(null);
  const [pomoDuration, setPomoDuration] = useState(initialSettings.workDuration);
  const [showStopModal, setShowStopModal] = useState(false);
  const [startHint, setStartHint]       = useState('');   // 没选任务就点开始时的提示
  const openStopModalRef = useRef(null);   // tick 里要调它，但它定义在后面
  const [stopNotes, setStopNotes]       = useState('');

  const [settings, setSettings]         = useState(initialSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [notifPerm, setNotifPerm]       = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  // Rest-mode task checklist — server-backed so every device sees the same set.
  const [restTasksState, setRestTasksState] = useState({ daily: {}, cooldown: {} });

  const isRestTaskAvailable = (t) => {
    if (t.cooldownDays) {
      const last = restTasksState.cooldown?.[t.id] || 0;
      if (!last) return true;
      return (Date.now() - last) >= t.cooldownDays * 86400000;
    }
    const list = restTasksState.daily?.[restTodayKey()] || [];
    return !list.includes(t.id);
  };

  const restTaskStatusText = (t) => {
    if (t.cooldownDays) {
      const last = restTasksState.cooldown?.[t.id] || 0;
      if (!last) return `${t.title} · 待执行`;
      const daysLeft = Math.ceil((last + t.cooldownDays * 86400000 - Date.now()) / 86400000);
      return daysLeft <= 0 ? `${t.title} · 该做了` : `${t.title} · 还有 ${daysLeft} 天`;
    }
    const list = restTasksState.daily?.[restTodayKey()] || [];
    return list.includes(t.id) ? `${t.title} · 已完成` : t.title;
  };

  const markRestTaskDone = (t) => {
    if (!isRestTaskAvailable(t)) return;
    const next = {
      daily: { ...(restTasksState.daily || {}) },
      cooldown: { ...(restTasksState.cooldown || {}) },
    };
    if (t.cooldownDays) {
      next.cooldown[t.id] = Date.now();
    } else {
      const today = restTodayKey();
      const set = new Set(next.daily[today] || []);
      set.add(t.id);
      next.daily[today] = [...set];
    }
    setRestTasksState(next);
    axios.put(`${API}/pomo/rest-tasks`, next, { headers: authHeaders() }).catch(() => {});
  };

  // Refresh permission when the settings panel opens (user may have changed
  // it in browser site-settings between renders).
  useEffect(() => {
    if (showSettings && 'Notification' in window) {
      setNotifPerm(Notification.permission);
    }
  }, [showSettings]);

  const requestNotif = async () => {
    if (!('Notification' in window)) {
      alert('这个浏览器不支持桌面通知。');
      return;
    }
    // Always sync state with the actual browser permission at click time.
    const current = Notification.permission;
    setNotifPerm(current);
    // Always dispatch the in-app toast — guarantees visible feedback even
    // if the OS-level notification is silently suppressed by Focus Assist,
    // Do Not Disturb, or a browser bug.
    const showAppToast = () => window.dispatchEvent(new CustomEvent('pomo-ended', {
      detail: { projectTitle: '测试通知', taskTitle: '这就是番茄结束时的样子' },
    }));

    if (current === 'denied') {
      alert('浏览器已阻止通知。请点地址栏左边的锁头图标 → 通知 → 允许，然后刷新。');
      return;
    }

    let perm = current;
    if (perm === 'default') {
      try {
        perm = await Notification.requestPermission();
        setNotifPerm(perm);
      } catch (e) {
        alert('请求通知权限失败：' + (e && e.message ? e.message : e));
        return;
      }
      if (perm !== 'granted') {
        alert('你没有允许通知。当前状态：' + perm);
        return;
      }
    }

    showAppToast();
    try {
      await showOSNotification('🍅 番茄测试通知', {
        body: '浏览器最小化 / 切到其他应用时也应该看到这条。\n如果只看到网页内 toast，请检查系统通知设置。',
        requireInteraction: true,
        tag: 'pomo-test-' + Date.now(),
        data: { url: '/sb' },
      });
    } catch (e) {
      const isMac = /Mac/i.test(navigator.platform);
      const macHint = '\n\n[macOS Chrome]\n' +
        '• 系统设置 → 通知 → Google Chrome → 允许通知 (Alerts / 横幅)\n' +
        '• 关掉「专注模式」/「勿扰」\n' +
        '• Chrome: chrome://settings/content/notifications 确认站点是允许';
      const winHint = '\n\n[Windows]\n' +
        '• Windows 设置 → 通知 → 你的浏览器 未开启\n' +
        '• 系统开启了「专注助手」';
      alert('创建系统通知失败：' + (e && e.message ? e.message : e) +
        (isMac ? macHint : winHint));
    }
  };

  const tickRef        = useRef(null);
  const taskListRefs   = useRef({});
  const restHistoryRef = useRef([]);
  const restStartRef   = useRef(null);    // wall-clock start of a rest interval
  const workEndFiredRef = useRef(false);  // guard: end-of-work logic runs once per timer
  const overtimeMarkRef = useRef(0);      // 已经发过第几次超时提醒
  const autoFinishRef   = useRef(null);   // tick 里要停番茄，但 confirmStop 定义在后面
  const submittingRef  = useRef(false);   // double-submit guard

  // ── Time-based per-project allocation ─────────────────────
  // At every switch/pause/stop we credit the elapsed time since the last
  // budgeting event to whichever projects were pending during that
  // interval, split evenly among them. Persisted in localStorage so it
  // survives page navigation. Cleared on stop.
  const allocRef       = useRef({});      // { projectTitle: { color, minutes } }
  const allocLastAtRef = useRef(0);       // ms timestamp; 0 = paused/idle
  const ALLOC_KEY    = 'pomo-alloc-current';
  const ALLOC_TS_KEY = 'pomo-alloc-last-at';

  const persistAlloc = () => {
    try {
      localStorage.setItem(ALLOC_KEY, JSON.stringify(allocRef.current));
      localStorage.setItem(ALLOC_TS_KEY, String(allocLastAtRef.current));
    } catch {}
  };

  const clearAlloc = () => {
    allocRef.current = {};
    allocLastAtRef.current = 0;
    try {
      localStorage.removeItem(ALLOC_KEY);
      localStorage.removeItem(ALLOC_TS_KEY);
    } catch {}
  };

  const budgetAlloc = (curProjects) => {
    const now = Date.now();
    if (allocLastAtRef.current > 0) {
      const deltaMin = (now - allocLastAtRef.current) / 60000;
      if (deltaMin > 0) {
        const active = {};
        (curProjects || []).forEach(p => {
          p.tasks.forEach(t => {
            if (t.pending) active[p.title] = p.color || null;
          });
        });
        const titles = Object.keys(active);
        if (titles.length > 0) {
          const per = deltaMin / titles.length;
          titles.forEach(title => {
            if (!allocRef.current[title]) allocRef.current[title] = { color: active[title], minutes: 0 };
            allocRef.current[title].minutes += per;
          });
        }
      }
    }
    allocLastAtRef.current = now;
    persistAlloc();
  };

  // Snapshot the current allocation + any in-flight interval into a
  // rounded {title, color, minutes}[] whose sum is `totalMin`.
  const allocSnapshot = (curProjects, totalMin) => {
    const now = Date.now();
    const acc = {};
    Object.entries(allocRef.current).forEach(([t, v]) => { acc[t] = { color: v.color, minutes: v.minutes }; });
    if (allocLastAtRef.current > 0 && !timerState.pausedAt) {
      const deltaMin = (now - allocLastAtRef.current) / 60000;
      if (deltaMin > 0) {
        const active = {};
        (curProjects || []).forEach(p => {
          p.tasks.forEach(t => {
            if (t.pending) active[p.title] = p.color || null;
          });
        });
        const titles = Object.keys(active);
        if (titles.length > 0) {
          const per = deltaMin / titles.length;
          titles.forEach(title => {
            if (!acc[title]) acc[title] = { color: active[title], minutes: 0 };
            acc[title].minutes += per;
          });
        }
      }
    }
    const raw = Object.entries(acc)
      .map(([title, v]) => ({ title, color: v.color, minutes: v.minutes }))
      .filter(p => p.minutes > 0.01);
    if (raw.length === 0) return [{ title: '未分类', color: null, minutes: Math.max(1, totalMin) }];
    let list = raw.map(p => ({ ...p, minutes: Math.max(1, Math.round(p.minutes)) }));
    const sum = list.reduce((s, p) => s + p.minutes, 0);
    const diff = totalMin - sum;
    if (diff !== 0) {
      const idx = list.reduce((bi, p, i) => p.minutes > list[bi].minutes ? i : bi, 0);
      list[idx].minutes = Math.max(1, list[idx].minutes + diff);
    }
    return list;
  };

  // Restore accumulated allocations on mount (survives page navigation)
  useEffect(() => {
    try {
      allocRef.current = JSON.parse(localStorage.getItem(ALLOC_KEY) || '{}');
    } catch { allocRef.current = {}; }
    allocLastAtRef.current = parseInt(localStorage.getItem(ALLOC_TS_KEY) || '0', 10) || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [dragIdx, setDragIdx] = useState(null);
  const [ipOrder, setIpOrder] = useState([]);

  useEffect(() => {
    localStorage.setItem('pomo-settings', JSON.stringify(settings));
    setNoiseVolume(settings.noiseVolume);
  }, [settings]);

  // When the user changes workDuration in settings, sync the idle countdown
  // display so it doesn't show a stale value until they hit start.
  useEffect(() => {
    if (!timerState.running && timerMode === 'work') {
      setPomoDuration(settings.workDuration);
      setTimeLeft(settings.workDuration * 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.workDuration]);

  const load = useCallback(async () => {
    try {
      const [pRes, sRes, tRes] = await Promise.all([
        axios.get(`${API}/pomo/projects`, { headers: authHeaders() }),
        axios.get(`${API}/pomo/sessions`,  { headers: authHeaders() }),
        axios.get(`${API}/pomo/timer`,     { headers: authHeaders() }),
      ]);
      setProjects(pRes.data);
      setSessions(sRes.data);
      const t = tRes.data;
      setTimerState(t);
      if (t.running) {
        const elapsed = Math.floor((Date.now() - new Date(t.startTime)) / 1000);
        setTimeLeft(Math.max(0, t.duration * 60 - elapsed));
        setPomoDuration(t.duration);
        if (t.taskId) setPendingTask({
          taskId: t.taskId, taskTitle: t.taskTitle,
          projectTitle: t.projectTitle, projectColor: t.projectColor || null,
        });
        // Restore background noise if timer was running before refresh
        const s = loadSettings();
        if (s.noiseType !== 'none') startNoise(s.noiseType, s.noiseVolume);
      }
    } catch (e) {
      if (e.response?.status === 401) { setLoggedIn(false); localStorage.removeItem('token'); }
    }
  }, []);

  useEffect(() => { if (loggedIn) load(); }, [loggedIn, load]);

  // ── Recurring tasks ──────────────────────────────────────────────
  // Each project can carry a list of recurring-task templates (title +
  // which weekdays). On the first load each session, any template due
  // today that hasn't already been turned into a real task today gets
  // POSTed as one — from then on it's just a normal task, indistinguishable
  // from one typed by hand. lastMaterialized guards it to once per day per
  // template: if the resulting task gets deleted later that same day, it
  // deliberately does NOT come back until the next matching day, rather
  // than resurrecting itself on every reload.
  const materializedRef = useRef(false);
  useEffect(() => {
    if (materializedRef.current || projects.length === 0) return;
    materializedRef.current = true;
    (async () => {
      const todayStr = new Date().toISOString().slice(0, 10);
      const todayDow = new Date().getDay();
      for (const proj of projects) {
        const templates = proj.recurringTasks || [];
        const due = templates.filter(t => t.days.includes(todayDow) && t.lastMaterialized !== todayStr);
        if (due.length === 0) continue;
        for (const tpl of due) {
          try { await axios.post(`${API}/pomo/projects/${proj.id}/tasks`, { title: tpl.title }, { headers: authHeaders() }); } catch {}
        }
        const nextTemplates = templates.map(t =>
          due.includes(t) ? { ...t, lastMaterialized: todayStr } : t);
        try { await axios.put(`${API}/pomo/projects/${proj.id}`, { recurringTasks: nextTemplates }, { headers: authHeaders() }); } catch {}
      }
      load();
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const addRecurringTask = async (proj) => {
    if (!newRecurTitle.trim() || newRecurDays.length === 0) return;
    const next = [...(proj.recurringTasks || []), {
      id: Date.now(), title: newRecurTitle.trim(), days: [...newRecurDays].sort(), lastMaterialized: null,
    }];
    await axios.put(`${API}/pomo/projects/${proj.id}`, { recurringTasks: next }, { headers: authHeaders() });
    setNewRecurTitle(''); setNewRecurDays([]);
    load();
  };

  const deleteRecurringTask = async (proj, id) => {
    const next = (proj.recurringTasks || []).filter(t => t.id !== id);
    await axios.put(`${API}/pomo/projects/${proj.id}`, { recurringTasks: next }, { headers: authHeaders() });
    load();
  };

  // Fetch server rest-tasks state on login; one-time merge with any lingering
  // localStorage state so we don't lose per-device history the first time.
  useEffect(() => {
    if (!loggedIn) return;
    axios.get(`${API}/pomo/rest-tasks`, { headers: authHeaders() })
      .then(res => {
        const server = res.data || { daily: {}, cooldown: {} };
        const { merged, changed } = mergeLocalIntoRestState(server);
        setRestTasksState(merged);
        if (changed) {
          axios.put(`${API}/pomo/rest-tasks`, merged, { headers: authHeaders() })
            .then(() => clearLocalRestTaskKeys())
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [loggedIn]);

  // Listen for manual session saves from StatsPanel
  useEffect(() => {
    window.addEventListener('pomo-reload', load);
    return () => window.removeEventListener('pomo-reload', load);
  }, [load]);

  // Newest tasks render at the top now, so the natural scroll position (0)
  // already shows them. No auto-scroll needed on load.

  // Timer tick — wall-clock based, so backgrounded tabs (which throttle
  // setInterval) never drift out of sync. Chime is played by the global
  // PomoWatcher so it fires regardless of which page you're on.
  useEffect(() => {
    if (!(timerState.running || timerMode === 'rest')) {
      clearInterval(tickRef.current);
      return;
    }

    const compute = () => {
      const durSec = pomoDuration * 60;
      // While paused, treat `pausedAt` as "now" so the display freezes.
      const now = timerState.pausedAt
        ? new Date(timerState.pausedAt).getTime()
        : Date.now();
      const startMs = timerMode === 'rest'
        ? (restStartRef.current || now)
        : (timerState.startTime ? new Date(timerState.startTime).getTime() : now);
      return durSec - Math.floor((now - startMs) / 1000);
    };

    // If we mounted after the timer had already expired (e.g. returning to
    // this page much later), don't re-trigger the end-of-work logic.
    if (timerMode === 'work' && compute() <= 0) workEndFiredRef.current = true;

    const tick = () => {
      const remaining = compute();
      setTimeLeft(remaining);

      if (timerMode === 'rest' && remaining <= 0) {
        clearInterval(tickRef.current);
        endRest();
        return;
      }

      if (timerMode === 'work' && remaining <= 0) {
        if (!workEndFiredRef.current) {
          workEndFiredRef.current = true;
          stopNoise();
          // 自动休息：必须先把番茄真正结束掉（停服务端计时、记一条 session），
          // 再进休息。以前这里只是把 timerMode 切成 'rest'，番茄还在后台跑，
          // 休息完回来一看已经攒了好几个小时。
          if (settings.autoRest) {
            autoFinishRef.current?.(true);
            return;
          }
          if (settings.autoStop) {
            openStopModalRef.current?.();
            // 不 clearInterval：弹窗开着但没人点时，下面的硬上限还得继续守着
          }
        }

        // 超时提醒：每 OVERTIME_STEP_MIN 分钟一次，到第 OVERTIME_MAX_MARKS 次强制结束
        const mark = Math.floor(-remaining / (OVERTIME_STEP_MIN * 60));
        if (mark >= 1 && mark > overtimeMarkRef.current) {
          overtimeMarkRef.current = mark;
          const totalMin = pomoDuration + mark * OVERTIME_STEP_MIN;
          const last = mark >= OVERTIME_MAX_MARKS;
          if (settings.alertSounds) { try { playEndChime(); } catch {} }
          showOSNotification(
            last ? '🍅 番茄自动结束' : '🍅 番茄已超时',
            {
              body: last
                ? `已经 ${totalMin} 分钟，自动帮你结束了这个番茄。`
                : `番茄已结束，现在已经 ${totalMin} 分钟了。还在继续吗？`,
              tag: 'pomo-overtime',
            }
          );
          if (last) {
            autoFinishRef.current?.(true, pomoDuration + OVERTIME_MAX_MARKS * OVERTIME_STEP_MIN);
            return;
          }
        }
      }
    };

    tick();
    tickRef.current = setInterval(tick, 500);

    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(tickRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [timerState.running, timerState.startTime, timerState.pausedAt, timerMode, pomoDuration, settings]); // eslint-disable-line

  // Auth
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem('token', res.data.token);
      setLoggedIn(true); setLoginError('');
    } catch { setLoginError('密码不正确'); }
  };

  // Timer
  const startTimer = async () => {
    if (submittingRef.current) return;
    const allPending = projects.flatMap(p => p.tasks.filter(t => t.pending).map(t => ({ taskId: t.id, taskTitle: t.title, projectTitle: p.title, projectColor: p.color })));
    // 以前按钮是 disabled，点了毫无反应，看起来像坏了。改成给一句话。
    if (allPending.length === 0) {
      setStartHint('请先点选一个任务再开始');
      setTimeout(() => setStartHint(''), 3000);
      return;
    }
    setStartHint('');
    submittingRef.current = true;
    workEndFiredRef.current = false;
    overtimeMarkRef.current = 0;
    // Ask for OS notification permission on the click gesture (browsers
    // require a user gesture for the prompt to appear).
    if ('Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch {}
    }
    // Group selected tasks by project so the stop handler can split the
    // pomodoro's duration evenly across every project involved.
    const projMap = {};
    allPending.forEach(t => {
      const key = t.projectTitle;
      if (!projMap[key]) projMap[key] = { title: t.projectTitle, color: t.projectColor || null, tasks: [] };
      projMap[key].tasks.push(t.taskTitle);
    });
    const projectsList = Object.values(projMap);
    // Dominant project = project with most pending tasks (used for the
    // timer-panel display + as the fallback if the server drops projectsList).
    const projCount = {};
    allPending.forEach(t => { projCount[t.projectTitle] = (projCount[t.projectTitle] || 0) + 1; });
    const primary = allPending.reduce((best, t) => projCount[t.projectTitle] > projCount[best.projectTitle] ? t : best, allPending[0]);
    const dur = settings.workDuration;
    try {
      const res = await axios.post(`${API}/pomo/timer/start`, {
        duration: dur,
        taskId: primary.taskId,
        taskTitle: allPending.map(t => t.taskTitle).join('，'),
        projectTitle: primary.projectTitle,
        projectColor: primary.projectColor || null,
        projects: projectsList,
      }, { headers: authHeaders() });
      setTimerState(res.data);
      setPomoDuration(dur);
      setTimeLeft(dur * 60);
      setTimerMode('work');
      setSessionTasks(allPending.map(t => ({ ...t, startedAt: Date.now() })));
      // Reset allocation tracking for the new pomodoro
      clearAlloc();
      allocLastAtRef.current = Date.now();
      persistAlloc();
      if (settings.alertSounds) playStartChime();
      if (settings.noiseType !== 'none') startNoise(settings.noiseType, settings.noiseVolume);
      window.dispatchEvent(new Event('pomo-timer-changed'));
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        setLoggedIn(false);
      } else {
        console.error('番茄钟启动失败:', e);
        alert('启动失败：' + (e.response?.data?.message || e.message || '请检查网络后重试'));
      }
    } finally {
      submittingRef.current = false;
    }
  };

  // capMin：自动结束时用。隔了几小时才回到页面的话，真实流逝时间是几小时，
  // 原样记下来就是用户抱怨的「几百小时的番茄」。超时自动结束时按
  // 计划时长 + 允许的超时上限封顶，多出来的那段不算工作。
  const confirmStop = async (completed, capMin) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    clearInterval(tickRef.current);
    stopNoise();
    const elapsed = timerState.startTime
      ? Math.max(1, Math.round((Date.now() - new Date(timerState.startTime).getTime()) / 60000))
      : pomoDuration;
    const actualDuration = capMin ? Math.min(elapsed, capMin) : elapsed;

    // Credit the final interval, then read the accumulated per-project
    // allocation. The split reflects who was pending across the whole
    // pomo — a task selected in the last second only earns its share of
    // that second, not an even split of the whole duration.
    budgetAlloc(projects);
    const projectsList = allocSnapshot(projects, Math.max(1, actualDuration));

    try {
      await axios.post(`${API}/pomo/timer/stop`, {
        notes: stopNotes,
        completed,
        actualDuration,
        projects: projectsList.length > 0 ? projectsList : undefined,
      }, { headers: authHeaders() });
      clearAlloc();
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        setLoggedIn(false);
      } else {
        console.error('番茄钟停止保存失败:', e);
      }
    }

    // 清空所有选中：pending 是存在服务端每个任务上的，不清的话会一直累积到
    // 下一天、下一次，表现为「我没选，却已经有几个是选中的」。
    await clearAllPending();

    // Local cleanup (always runs so the UI doesn't get stuck on a server error)
    setTimerState({ running: false });
    setShowStopModal(false);
    setStopNotes('');
    setSessionTasks([]);
    workEndFiredRef.current = false;
    overtimeMarkRef.current = 0;
    window.dispatchEvent(new Event('pomo-timer-changed'));

    // 只有开了「自动休息」才接着进休息界面。关掉时番茄结束就是结束，
    // 不该冒出一个我没要求的休息界面。手动休息仍可用下面那排按钮。
    if (completed && settings.autoRest) {
      // Auto-jump to rest mode: long break if 3+ recent shorts, otherwise short.
      const now = Date.now();
      const recent = restHistoryRef.current.filter(t => now - t < 3 * 3600000);
      const isLong = recent.length >= 3;
      const breakDur = isLong ? settings.longBreak : settings.shortBreak;
      if (!isLong) restHistoryRef.current.push(now);
      else restHistoryRef.current = [];
      restStartRef.current = Date.now();
      setTimerMode('rest');
      setPomoDuration(breakDur);
      setTimeLeft(breakDur * 60);
      if (settings.alertSounds) playBreakChime();
    } else {
      // Aborted — reset to work-mode idle.
      setPomoDuration(settings.workDuration);
      setTimeLeft(settings.workDuration * 60);
      setTimerMode('work');
    }
    load();
    submittingRef.current = false;
  };

  // tick 在 confirmStop 之前定义，只能通过 ref 回调
  autoFinishRef.current = confirmStop;

  const startRest = (dur) => {
    stopNoise();
    restStartRef.current = Date.now();
    setTimerMode('rest');
    setPomoDuration(dur);
    setTimeLeft(dur * 60);
    if (settings.alertSounds) playBreakChime();
  };

  const endRest = () => {
    restStartRef.current = null;
    setTimerMode('work');
    setPomoDuration(settings.workDuration);
    setTimeLeft(settings.workDuration * 60);
  };

  // Projects
  const addProject = async (e) => {
    e.preventDefault();
    if (!newProjectTitle.trim()) return;
    await axios.post(`${API}/pomo/projects`, { title: newProjectTitle, color: newProjectColor }, { headers: authHeaders() });
    setNewProjectTitle(''); setShowNewProject(false); load();
  };

  const deleteProject = async (id) => {
    if (!window.confirm('删除这个项目？')) return;
    await axios.delete(`${API}/pomo/projects/${id}`, { headers: authHeaders() });
    load();
  };

  // Tasks
  const addTask = async (e, projId) => {
    e.preventDefault();
    const title = newTaskInputs[projId]?.trim();
    if (!title || submittingRef.current) return;
    submittingRef.current = true;
    setNewTaskInputs(prev => ({ ...prev, [projId]: '' }));
    const tempId = 'temp-' + Date.now();
    setProjects(prev => prev.map(p => p.id === projId
      ? { ...p, tasks: [...p.tasks, { id: tempId, title, status: 'todo', pending: false }] }
      : p
    ));
    try {
      await axios.post(`${API}/pomo/projects/${projId}/tasks`, { title }, { headers: authHeaders() });
      load();
    } catch { load(); }
    finally { submittingRef.current = false; }
  };

  // 改项目颜色：先本地生效，再同步到服务端
  const setProjectColor = async (proj, color) => {
    setProjects(prev => prev.map(p => p.id === proj.id ? { ...p, color } : p));
    try {
      await axios.put(`${API}/pomo/projects/${proj.id}`, { color }, { headers: authHeaders() });
    } catch { load(); }
  };

  // 直接把任务设成指定状态（cycleStatus 只能沿 todo→doing→done 转圈，
  // 从进行中退回未开始需要能指定目标）
  const setStatus = async (proj, task, next) => {
    if (task.pending && pendingTask?.taskId === task.id) setPendingTask(null);
    setProjects(prev => prev.map(p => p.id === proj.id
      ? { ...p, tasks: p.tasks.map(t => t.id === task.id ? { ...t, status: next, pending: false } : t) }
      : p
    ));
    try {
      await axios.put(`${API}/pomo/projects/${proj.id}/tasks/${task.id}`,
        { status: next, pending: false }, { headers: authHeaders() });
    } catch { load(); }
  };

  const cycleStatus = async (proj, task) => {
    const next = STATUS_CYCLE[task.status] || 'todo';
    if (task.pending && pendingTask?.taskId === task.id) setPendingTask(null);
    setProjects(prev => prev.map(p => p.id === proj.id
      ? { ...p, tasks: p.tasks.map(t => t.id === task.id ? { ...t, status: next, pending: false } : t) }
      : p
    ));
    try {
      await axios.put(`${API}/pomo/projects/${proj.id}/tasks/${task.id}`,
        { status: next, pending: false }, { headers: authHeaders() });
    } catch { load(); }
  };

  // 取消所有任务的选中态（本地先改，再逐个同步到服务端）
  const clearAllPending = async () => {
    const stale = projects.flatMap(p => p.tasks.filter(t => t.pending).map(t => ({ p, t })));
    if (stale.length === 0) return;
    setProjects(prev => prev.map(p => ({ ...p, tasks: p.tasks.map(t => ({ ...t, pending: false })) })));
    setPendingTask(null);
    try {
      await Promise.all(stale.map(({ p, t }) =>
        axios.put(`${API}/pomo/projects/${p.id}/tasks/${t.id}`, { pending: false }, { headers: authHeaders() })));
    } catch { /* 本地已清干净，下次 load() 会对齐 */ }
  };

  // Toggle a task in/out of the pending (selected) set — max 5
  const togglePending = async (proj, task) => {
    const allPending = projects.flatMap(p => p.tasks.filter(t => t.pending));
    const isAlready = task.pending;
    if (!isAlready && allPending.length >= 5) {
      setStartHint('一轮番茄最多选 5 个任务');
      setTimeout(() => setStartHint(''), 3000);
      return;
    }
    // 运行中换任务：先把到此刻为止的时间结算给原来那批，否则新选的任务
    // 会白捡前面已经过去的时间。
    if (timerState.running) budgetAlloc(projects);

    // 选中就把它提成「进行中」，这样右侧列表一定能看到它。
    // 以前 pending 和 status 是两套东西，左边蓝了右边却没有，就是这么来的。
    const nextStatus = !isAlready && task.status === 'todo' ? 'in-progress' : task.status;

    setProjects(prev => prev.map(p => p.id === proj.id
      ? { ...p, tasks: p.tasks.map(t => t.id === task.id ? { ...t, pending: !isAlready, status: nextStatus } : t) }
      : p
    ));
    if (!isAlready) setPendingTask({ taskId: task.id, taskTitle: task.title, projectId: proj.id, projectTitle: proj.title, projectColor: proj.color });
    else if (pendingTask?.taskId === task.id) setPendingTask(null);
    try {
      await axios.put(`${API}/pomo/projects/${proj.id}/tasks/${task.id}`,
        { pending: !isAlready, status: nextStatus }, { headers: authHeaders() });
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('token'); setLoggedIn(false); }
      else load();
    }
  };

  // During timer: toggle task in/out of active set (no API, local only).
  // Credit elapsed time to the CURRENT pending set before the toggle takes
  // effect, so the switch is precise to the moment it happens.
  // 运行中切任务。以前这里只改本地 state：不落库、没有 5 个上限、也不会把
  // 任务提成「进行中」，于是左边变蓝了右边却没有它。统一走 togglePending。
  const switchTask = (proj, task) => togglePending(proj, task);

  // ── Pause / resume ─────────────────────
  const pauseTimer = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const res = await axios.post(`${API}/pomo/timer/pause`, {}, { headers: authHeaders() });
      setTimerState(res.data);
      // Flush accumulated time up to the pause moment, then stop accruing
      budgetAlloc(projects);
      allocLastAtRef.current = 0;
      persistAlloc();
      stopNoise();
      window.dispatchEvent(new Event('pomo-timer-changed'));
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('token'); setLoggedIn(false); }
      else console.error('暂停失败:', e);
    } finally { submittingRef.current = false; }
  };

  const resumeTimer = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const res = await axios.post(`${API}/pomo/timer/resume`, {}, { headers: authHeaders() });
      setTimerState(res.data);
      // Restart the allocation clock at the resume moment
      allocLastAtRef.current = Date.now();
      persistAlloc();
      if (settings.noiseType !== 'none') startNoise(settings.noiseType, settings.noiseVolume);
      window.dispatchEvent(new Event('pomo-timer-changed'));
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('token'); setLoggedIn(false); }
      else console.error('继续失败:', e);
    } finally { submittingRef.current = false; }
  };

  const deleteTask = async (proj, task) => {
    if (task.pending && pendingTask?.taskId === task.id) setPendingTask(null);
    await axios.delete(`${API}/pomo/projects/${proj.id}/tasks/${task.id}`, { headers: authHeaders() });
    load();
  };

  // One-click clear all "done" tasks across EVERY project (single global
  // button, not per-column). No confirm dialog — that's the point of
  // "one-click".
  const clearAllDoneTasks = async () => {
    if (!projects.some(p => p.tasks.some(t => t.status === 'done'))) return;
    setProjects(prev => prev.map(p => ({ ...p, tasks: p.tasks.filter(t => t.status !== 'done') })));
    try {
      await axios.delete(`${API}/pomo/tasks-done`, { headers: authHeaders() });
    } catch { load(); }
  };

  // ── Task drag-reorder (within a single project's column only) ──────
  // taskDrag tracks { projId, idx } where idx is the index into the
  // DISPLAYED (newest-first) list for that project. Cross-project drags
  // are ignored — onDragOver below early-returns when projId differs.
  const [taskDrag, setTaskDrag] = useState(null);

  // Every task card is draggable="true" for reordering — which, in every
  // browser, hijacks a click-drag over the title text into picking up the
  // whole card instead of selecting text, so copying a task's title by
  // hand-selecting it doesn't work. One-click copy button sidesteps that
  // entirely instead of fighting the drag gesture.
  const [copiedTaskId, setCopiedTaskId] = useState(null);
  const copyTaskTitle = async (task) => {
    try {
      await navigator.clipboard.writeText(task.title);
      setCopiedTaskId(task.id);
      setTimeout(() => setCopiedTaskId(id => (id === task.id ? null : id)), 1200);
    } catch {}
  };

  const reorderTaskLocal = (proj, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    // Work in DISPLAY order (newest-first), then flip back to storage order.
    const display = [...proj.tasks].reverse();
    const [moved] = display.splice(fromIdx, 1);
    display.splice(toIdx, 0, moved);
    const storageOrder = [...display].reverse();
    setProjects(prev => prev.map(p => p.id === proj.id ? { ...p, tasks: storageOrder } : p));
  };

  const persistTaskOrder = async (projId) => {
    const proj = projects.find(p => p.id === projId);
    if (!proj) return;
    try {
      await axios.put(`${API}/pomo/projects/${projId}/tasks-reorder`,
        { orderedIds: proj.tasks.map(t => t.id) }, { headers: authHeaders() });
    } catch { load(); }
  };

  const openEditSession = (s) => {
    setEditingSessionFull(s.id);
    const st = s.startTime ? new Date(s.startTime) : new Date();
    const offset = st.getTimezoneOffset() * 60000;
    const localISO = new Date(st.getTime() - offset).toISOString().slice(0, 16);
    // Convert to per-project rows. Legacy rows (no `projects` array) get a
    // single row from the primary fallback.
    const rows = Array.isArray(s.projects) && s.projects.length > 0
      ? s.projects.map(p => ({ title: p.title || '', color: p.color || null, minutes: p.minutes || 0 }))
      : [{ title: s.projectTitle || '', color: s.projectColor || null, minutes: s.duration || 0 }];
    setEditSessionData({
      projects: rows,
      startTime: localISO,
      notes: s.notes || '',
      completed: s.completed ?? true,
    });
  };

  const saveEditSession = async (e) => {
    e.preventDefault();
    // Drop empty rows; snap colors to the current project palette in case
    // the user changed a row's project (or added a new row).
    const cleaned = (editSessionData.projects || [])
      .filter(p => (p.title || '').trim() !== '' && (parseInt(p.minutes, 10) || 0) > 0)
      .map(p => ({
        title: p.title,
        color: projects.find(pr => pr.title === p.title)?.color
          || EXTRA_PROJECT_OPTIONS.find(pr => pr.title === p.title)?.color
          || p.color || null,
        minutes: parseInt(p.minutes, 10) || 0,
      }));
    const finalProjs = cleaned.length > 0 ? cleaned : [{ title: '未分类', color: null, minutes: 1 }];
    const totalMin = finalProjs.reduce((s, p) => s + p.minutes, 0);
    await axios.put(`${API}/pomo/sessions/${editingSessionFull}`, {
      projects: finalProjs,
      projectTitle: finalProjs[0].title,           // legacy fallback fields
      projectColor: finalProjs[0].color,
      duration: totalMin,
      startTime: new Date(editSessionData.startTime).toISOString(),
      notes: editSessionData.notes,
      completed: editSessionData.completed,
    }, { headers: authHeaders() });
    setEditingSessionFull(null);
    load();
  };

  // Helpers for the multi-row edit form
  const editRowUpdate = (i, patch) => setEditSessionData(d => ({
    ...d,
    projects: d.projects.map((p, j) => j === i ? { ...p, ...patch } : p),
  }));
  const editRowRemove = (i) => setEditSessionData(d => ({
    ...d,
    projects: d.projects.filter((_, j) => j !== i),
  }));
  const editRowAdd = () => setEditSessionData(d => ({
    ...d,
    projects: [...(d.projects || []), { title: '', color: null, minutes: 0 }],
  }));

  const deleteSession = async (id) => {
    await axios.delete(`${API}/pomo/sessions/${id}`, { headers: authHeaders() });
    load();
  };

  // Helpers
  const fmt = s => {
    if (s < 0) {
      const abs = -s;
      return `+${String(Math.floor(abs/60)).padStart(2,'0')}:${String(abs%60).padStart(2,'0')}`;
    }
    return `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
  };
  const fmtDate = iso => new Date(iso).toLocaleString('zh-CN', { month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' });

  // Download all in-progress + done tasks as txt, one line per project
  const downloadTasks = () => {
    const lines = projects.map(proj => {
      const tasks = proj.tasks.filter(t => t.status === 'in-progress' || t.status === 'done');
      if (tasks.length === 0) return null;
      const names = tasks.map(t => t.status === 'done' ? `${t.title} ✓` : t.title);
      return `${proj.title}：${names.join('，')}`;
    }).filter(Boolean);
    const text = lines.length ? lines.join('\n') : '（暂无进行中或已完成的任务）';
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openStopModal = () => {
    const pending = projects.flatMap(p => p.tasks.filter(t => t.pending).map(t => ({ title: t.title, projTitle: p.title, projColor: p.color })));
    setStopNotes(pending.map(t => t.title).join('，'));
    setShowStopModal(true);
  };
  openStopModalRef.current = openStopModal;

  const isRunning     = timerState.running || timerMode === 'rest';
  const isOvertime    = timerMode === 'work' && timeLeft < 0;
  const progress      = timerMode === 'work'
    ? Math.min(1, 1 - timeLeft / (pomoDuration * 60))
    : 1 - timeLeft / (pomoDuration * 60);
  const circumference = 2 * Math.PI * 54;
  const ringColor     = isOvertime ? '#ff6b6b'
    : timerMode === 'rest' ? '#ffab40'
    : (isRunning ? '#64ffda' : '#334');

  const inProgressTasksRaw = projects.flatMap(proj =>
    proj.tasks.filter(t => t.status === 'in-progress').map(t => ({ ...t, projId: proj.id, projTitle: proj.title, projColor: proj.color }))
  );

  // Maintain user drag order
  const inProgressTasks = useMemo(() => {
    const byId = new Map(inProgressTasksRaw.map(t => [t.id, t]));
    const ordered = ipOrder.filter(id => byId.has(id)).map(id => byId.get(id));
    const rest = inProgressTasksRaw.filter(t => !ipOrder.includes(t.id));
    return [...ordered, ...rest];
  }, [inProgressTasksRaw, ipOrder]);

  // 这里原本会在「一个都没选」时自动替用户选上第一个进行中的任务，
  // 结果就是明明没点过，却发现已经有任务被选中。选择权交还给用户。

  if (!loggedIn) return (
    <div className="pomo-login-gate">
      <div className="pomo-login-box">
        <h2>Pomodoro Board</h2>
        <p>请输入密码访问</p>
        <form onSubmit={handleLogin}>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="密码" autoFocus />
          {loginError && <p className="pomo-login-error">{loginError}</p>}
          <button type="submit" className="btn btn-primary">进入</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="pomo-page">

      {recurringModalFor && (() => {
        const proj = projects.find(p => p.id === recurringModalFor);
        if (!proj) return null;
        const templates = proj.recurringTasks || [];
        const toggleDay = (d) => setNewRecurDays(prev =>
          prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
        return (
          <div className="pomo-modal-overlay" {...overlayCloseProps(() => setRecurringModalFor(null))}>
            <div className="pomo-modal" onClick={e => e.stopPropagation()}>
              <h3>{proj.title} · 周期性任务</h3>
              <p className="pomo-recur-hint">选中的星期几到了，当天会自动多出一条对应的任务。</p>
              {templates.length > 0 && (
                <ul className="pomo-recur-list">
                  {templates.map(t => (
                    <li key={t.id}>
                      <span className="pomo-recur-days">{t.days.map(d => WEEKDAY_LABELS[d]).join('')}</span>
                      <span className="pomo-recur-title">{t.title}</span>
                      <button className="pomo-icon-btn danger" onClick={() => deleteRecurringTask(proj, t.id)}>×</button>
                    </li>
                  ))}
                </ul>
              )}
              <form className="pomo-recur-form" onSubmit={e => { e.preventDefault(); addRecurringTask(proj); }}>
                <input value={newRecurTitle} onChange={e => setNewRecurTitle(e.target.value)}
                  placeholder="新的周期性任务" />
                <div className="pomo-recur-weekdays">
                  {WEEKDAY_LABELS.map((label, d) => (
                    <button key={d} type="button"
                      className={`pomo-recur-day-btn${newRecurDays.includes(d) ? ' selected' : ''}`}
                      onClick={() => toggleDay(d)}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="pomo-modal-actions">
                  <button type="submit" className="btn btn-primary btn-small"
                    disabled={!newRecurTitle.trim() || newRecurDays.length === 0}>添加</button>
                  <button type="button" className="btn btn-small" onClick={() => setRecurringModalFor(null)}>关闭</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {showStopModal && (() => {
        // Preview: same allocSnapshot the server will get. Reflects the
        // actual per-project time based on when each was selected — not a
        // naive even split of whoever's pending at this moment.
        const est = timerState.startTime
          ? Math.max(1, Math.round((Date.now() - new Date(timerState.startTime).getTime()) / 60000))
          : pomoDuration;
        const preview = allocSnapshot(projects, est);
        return (
          <div className="pomo-modal-overlay" {...overlayCloseProps(() => setShowStopModal(false))}>
            <div className="pomo-modal" onClick={e => e.stopPropagation()}>
              <h3>记录本次番茄</h3>
              {preview.length > 0 && (
                <div className="pomo-stop-preview">
                  {preview.map((p, i) => (
                    <span key={i} className="pomo-stop-chip" style={{ color: p.color || UNCATEGORIZED_COLOR }}>
                      {p.title}
                      <span className="pomo-stop-chip-mins">{p.minutes}m</span>
                    </span>
                  ))}
                </div>
              )}
              <textarea value={stopNotes} onChange={e => setStopNotes(e.target.value)} placeholder="这段时间做了什么？（可选）" rows={3} />
              <div className="pomo-modal-actions">
                <button className="btn btn-success" onClick={() => confirmStop(true)}>完成 ✓ → 休息</button>
                <button className="btn btn-danger"  onClick={() => confirmStop(false)}>中止</button>
                <button className="btn"             onClick={() => setShowStopModal(false)}>取消</button>
              </div>
            </div>
          </div>
        );
      })()}

      {editingSessionFull && (
        <div className="pomo-modal-overlay" {...overlayCloseProps(() => setEditingSessionFull(null))}>
          <div className="pomo-modal" onClick={e => e.stopPropagation()}>
            <h3>编辑记录</h3>
            <form onSubmit={saveEditSession} className="pomo-manual-form">
              <div className="pomo-edit-projects">
                <div className="pomo-edit-projects-header">
                  <span>项目分配</span>
                  <span className="pomo-edit-total">
                    合计 {(editSessionData.projects || []).reduce((s, p) => s + (parseInt(p.minutes, 10) || 0), 0)}m
                  </span>
                </div>
                {(editSessionData.projects || []).map((p, i) => (
                  <div key={i} className="pomo-edit-project-row">
                    <select
                      value={p.title}
                      onChange={e => {
                        const val = e.target.value;
                        const proj = projects.find(pr => pr.title === val)
                          || EXTRA_PROJECT_OPTIONS.find(pr => pr.title === val);
                        editRowUpdate(i, { title: val, color: proj?.color || null });
                      }}
                    >
                      <option value="">未分类</option>
                      {EXTRA_PROJECT_OPTIONS.map(opt => (
                        <option key={opt.title} value={opt.title}>{opt.title}</option>
                      ))}
                      {projects.map(pr => <option key={pr.id} value={pr.title}>{pr.title}</option>)}
                    </select>
                    <input
                      type="number" min="0" max="600"
                      value={p.minutes}
                      onChange={e => editRowUpdate(i, { minutes: e.target.value })}
                    />
                    <span className="pomo-edit-min-lbl">分钟</span>
                    <button
                      type="button"
                      className="pomo-edit-row-del"
                      onClick={() => editRowRemove(i)}
                      title="删除这项"
                    >×</button>
                  </div>
                ))}
                <button type="button" className="btn btn-small pomo-edit-add-row" onClick={editRowAdd}>+ 加一项</button>
              </div>
              <div className="pomo-manual-row">
                <label>开始时间</label>
                <input type="datetime-local" value={editSessionData.startTime} onChange={e => setEditSessionData(d => ({...d, startTime: e.target.value}))} />
              </div>
              <div className="pomo-manual-row">
                <label>备注</label>
                <input value={editSessionData.notes} onChange={e => setEditSessionData(d => ({...d, notes: e.target.value}))} placeholder="备注（可选）" />
              </div>
              <div className="pomo-manual-row">
                <label>状态</label>
                <select value={editSessionData.completed ? 'done' : 'aborted'} onChange={e => setEditSessionData(d => ({...d, completed: e.target.value === 'done'}))}>
                  <option value="done">完成</option>
                  <option value="aborted">中止</option>
                </select>
              </div>
              <div className="pomo-modal-actions">
                <button type="submit" className="btn btn-primary btn-small">保存</button>
                <button type="button" className="btn btn-small" onClick={() => setEditingSessionFull(null)}>取消</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="pomo-main">
        {/* Board */}
        <div className="pomo-board-wrap">
          <div className="pomo-board">
            {projects.map(proj => (
              <div className="pomo-column" key={proj.id}>
                <div className="pomo-col-header" style={{ borderTopColor: proj.color }}
                  onDoubleClick={() => setRecurringModalFor(proj.id)}
                  title="双击设置周期性任务">
                  <button className="pomo-col-dot" title="改颜色"
                    onClick={() => setColorPickerFor(v => v === proj.id ? null : proj.id)}>
                    <i style={{ background: proj.color }} />
                  </button>
                  <span className="pomo-col-title">{proj.title}</span>
                  <button className="pomo-icon-btn danger" onClick={() => deleteProject(proj.id)}>×</button>
                  {colorPickerFor === proj.id && (
                    <ColorWheel value={proj.color}
                      onPick={c => setProjectColor(proj, c)}
                      onClose={() => setColorPickerFor(null)} />
                  )}
                </div>
                <div className="pomo-task-list" ref={el => taskListRefs.current[proj.id] = el}>
                  {[...proj.tasks].reverse().map((task, idx) => (
                    <div key={task.id}
                      className={`pomo-task status-${task.status}${task.pending ? ' pending' : ''}${taskDrag?.projId === proj.id && taskDrag?.idx === idx ? ' dragging' : ''}`}
                      style={
                        task.pending
                          ? { borderColor: proj.color, boxShadow: `0 0 8px ${proj.color}44` }
                          : task.status === 'in-progress'
                          ? { borderColor: proj.color }
                          : {}
                      }
                      draggable
                      onDragStart={e => { e.stopPropagation(); setTaskDrag({ projId: proj.id, idx }); }}
                      onDragOver={e => {
                        e.preventDefault();
                        if (!taskDrag || taskDrag.projId !== proj.id || taskDrag.idx === idx) return;
                        reorderTaskLocal(proj, taskDrag.idx, idx);
                        setTaskDrag({ projId: proj.id, idx });
                      }}
                      onDragEnd={() => { const pid = taskDrag?.projId; setTaskDrag(null); if (pid) persistTaskOrder(pid); }}
                      onClick={() => { if (timerState.running) switchTask(proj, task); else togglePending(proj, task); }}
                      title={task.pending ? '取消选中' : '点击加入本轮番茄（最多5个）'}
                    >
                      <button className="pomo-icon-btn danger pomo-task-del" onClick={e => { e.stopPropagation(); deleteTask(proj, task); }}>×</button>
                      <button className={`pomo-status-btn s-${task.status}`} onClick={e => { e.stopPropagation(); cycleStatus(proj, task); }} title="切换状态">
                        {STATUS_ICON[task.status]}
                      </button>
                      <div className="pomo-task-top">
                        <span className="pomo-task-title">
                          {task.pending && <span className="pomo-pending-dot">▶ </span>}
                          {task.title}
                        </span>
                        <button className="pomo-task-copy" title="复制任务文字"
                          onClick={e => { e.stopPropagation(); copyTaskTitle(task); }}>
                          {copiedTaskId === task.id ? '✓' : '⧉'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <form className="pomo-add-task-form" onSubmit={e => addTask(e, proj.id)}>
                  <input value={newTaskInputs[proj.id] || ''} onChange={e => setNewTaskInputs(prev => ({ ...prev, [proj.id]: e.target.value }))} placeholder="+ 添加子任务" />
                </form>
              </div>
            ))}

            <div className="pomo-column pomo-add-col">
              {showNewProject ? (
                <form onSubmit={addProject} className="pomo-new-project-form">
                  <input value={newProjectTitle} onChange={e => setNewProjectTitle(e.target.value)} placeholder="项目名称" autoFocus />
                  <div className="pomo-color-grid">
                    {PROJECT_COLORS.map(c => (
                      <span key={c} className={`pomo-color-dot${newProjectColor===c?' selected':''}`} style={{ background: c }} onClick={() => setNewProjectColor(c)} />
                    ))}
                  </div>
                  <div className="pomo-modal-actions">
                    <button type="submit" className="btn btn-primary btn-small">创建</button>
                    <button type="button" className="btn btn-small" onClick={() => setShowNewProject(false)}>取消</button>
                  </div>
                </form>
              ) : (
                <>
                  <button className="pomo-add-project-btn" onClick={() => setShowNewProject(true)}>+ 新项目</button>
                  <button className="pomo-clear-done-btn" onClick={clearAllDoneTasks} title="清除所有项目中已完成的任务">
                    🧹 清除已完成
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Timer panel */}
        <div className="pomo-timer-panel">
          <div className="pomo-panel-toprow">
            <span className="pomo-timer-title">{timerMode === 'rest' ? '休息中' : '番茄钟'}</span>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              <button className="pomo-settings-toggle" onClick={downloadTasks} title="下载任务清单 (txt)">↓</button>
              <button className={`pomo-settings-toggle${showSettings?' active':''}`} onClick={() => setShowSettings(s => !s)} title="设置">⚙</button>
            </div>
          </div>

          {showSettings && (
            <div className="pomo-settings-panel">
              <div className="pomo-setting-row">
                <label>专注</label>
                <input type="number" min="1" max="120" value={settings.workDuration}
                  onChange={e => setSettings(s => ({ ...s, workDuration: +e.target.value }))} />
                <span>分钟</span>
              </div>
              <div className="pomo-setting-row">
                <label>短休息</label>
                <input type="number" min="1" max="60" value={settings.shortBreak}
                  onChange={e => setSettings(s => ({ ...s, shortBreak: +e.target.value }))} />
                <span>分钟</span>
              </div>
              <div className="pomo-setting-row">
                <label>长休息</label>
                <input type="number" min="1" max="60" value={settings.longBreak}
                  onChange={e => setSettings(s => ({ ...s, longBreak: +e.target.value }))} />
                <span>分钟</span>
              </div>
              <label className="pomo-setting-check">
                <input type="checkbox" checked={settings.autoRest}
                  onChange={e => setSettings(s => ({ ...s, autoRest: e.target.checked }))} />
                完成后自动休息
              </label>
              <div className="pomo-setting-divider" />
              <div className="pomo-setting-row">
                <label>环境音</label>
                <select value={settings.noiseType}
                  onChange={e => {
                    const t = e.target.value;
                    setSettings(s => ({ ...s, noiseType: t }));
                    if (timerState.running) {
                      if (t === 'none') stopNoise();
                      else startNoise(t, settings.noiseVolume);
                    }
                  }}>
                  {SOUND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {settings.noiseType !== 'none' && (
                <div className="pomo-setting-row">
                  <label>音量</label>
                  <input type="range" min="0" max="1" step="0.05"
                    value={settings.noiseVolume}
                    onChange={e => setSettings(s => ({ ...s, noiseVolume: +e.target.value }))}
                    className="pomo-volume-slider"
                  />
                </div>
              )}
              <label className="pomo-setting-check">
                <input type="checkbox" checked={settings.autoStop}
                  onChange={e => setSettings(s => ({ ...s, autoStop: e.target.checked }))} />
                时间到自动停止（不继续超时计时）
              </label>
              <label className="pomo-setting-check">
                <input type="checkbox" checked={settings.alertSounds}
                  onChange={e => setSettings(s => ({ ...s, alertSounds: e.target.checked }))} />
                开始/结束提示音
              </label>
              {'Notification' in window && (
                <div className="pomo-setting-divider" />
              )}
              {'Notification' in window && (
                <div className="pomo-setting-row" style={{ alignItems: 'center' }}>
                  <label>桌面通知</label>
                  <span style={{
                    fontSize: '0.8rem',
                    color: notifPerm === 'granted' ? '#5dd181'
                         : notifPerm === 'denied' ? '#e06c6c'
                         : '#ffab40',
                    flex: 1,
                  }}>
                    {notifPerm === 'granted' ? '✓ 已开启'
                     : notifPerm === 'denied' ? '✗ 已阻止（浏览器锁头图标 → 允许通知）'
                     : '未开启'}
                  </span>
                  {notifPerm !== 'denied' && (
                    <button type="button" className="btn btn-small" onClick={requestNotif}>
                      {notifPerm === 'granted' ? '测试' : '开启'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pomo-ring-wrap">
            <svg viewBox="0 0 120 120" className="pomo-ring-svg">
              <circle cx="60" cy="60" r="54" className="pomo-ring-bg" />
              <circle cx="60" cy="60" r="54" className="pomo-ring-fg"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - progress)}
                style={{ stroke: ringColor }}
              />
            </svg>
            <div className="pomo-ring-time" style={{ color: isOvertime ? '#ff6b6b' : timerMode === 'rest' ? '#ffab40' : undefined }}>
              {fmt(timeLeft)}
              {isOvertime && <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '2px' }}>超时</div>}
            </div>
          </div>

          {timerMode === 'rest' ? (
            <>
              <div className="pomo-rest-controls">
                <div className="pomo-rest-label">☕ 休息时间</div>
                <button className="btn btn-small" onClick={endRest}>跳过休息</button>
              </div>
              <div className="pomo-rest-tasks">
                <div className="pomo-rest-tasks-label">今日休息任务</div>
                <div className="pomo-rest-tasks-grid">
                  {REST_TASKS.map(t => {
                    const available = isRestTaskAvailable(t);
                    const statusText = restTaskStatusText(t);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`pomo-rest-task${available ? '' : ' done'}${t.cooldownDays ? ' cooldown' : ''}`}
                        onClick={() => markRestTaskDone(t)}
                        disabled={!available}
                        title={statusText}
                        aria-label={statusText}
                      >
                        {t.emoji}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              {!isRunning && (
                <div className="pomo-duration-row">
                  <button className={`pomo-dur-btn${pomoDuration===settings.workDuration?' active':''}`}
                    onClick={() => { setPomoDuration(settings.workDuration); setTimeLeft(settings.workDuration*60); }}>
                    {settings.workDuration}m
                  </button>
                  <button className={`pomo-dur-btn${pomoDuration===settings.shortBreak?' active':''}`}
                    onClick={() => startRest(settings.shortBreak)}>
                    休息{settings.shortBreak}m
                  </button>
                  <button className={`pomo-dur-btn${pomoDuration===settings.longBreak?' active':''}`}
                    onClick={() => startRest(settings.longBreak)}>
                    长休{settings.longBreak}m
                  </button>
                </div>
              )}

              {(() => {
                const selTasks = projects.flatMap(p => p.tasks.filter(t => t.pending).map(t => ({ title: t.title, projTitle: p.title, projColor: p.color })));
                return (
                  <div className="pomo-selected-task">
                    {selTasks.length === 0
                      ? <span className="pomo-no-task">↓ 从下方点选任务（最多5个）</span>
                      : selTasks.length === 1
                        ? <><span className="pomo-sel-project" style={{ color: selTasks[0].projColor }}>{selTasks[0].projTitle}</span><span className="pomo-sel-title">{selTasks[0].title}</span></>
                        : <><span className="pomo-sel-project">已选 {selTasks.length} 个任务</span><span className="pomo-sel-title">{selTasks.map(t => t.title).join('、')}</span></>
                    }
                  </div>
                );
              })()}

              {startHint && <div className="pomo-start-hint">{startHint}</div>}

              <div className="pomo-timer-btns">
                {!isRunning ? (
                  <button className="btn btn-primary" onClick={startTimer}>开始</button>
                ) : (
                  <>
                    {timerMode === 'work' && (
                      timerState.pausedAt
                        ? <button className="btn btn-primary" onClick={resumeTimer}>▶ 继续</button>
                        : <button className="btn"             onClick={pauseTimer}>⏸ 暂停</button>
                    )}
                    <button className="btn btn-danger" onClick={openStopModal}>停止</button>
                  </>
                )}
                {!isRunning && <button className="btn" onClick={() => setTimeLeft(pomoDuration*60)}>重置</button>}
              </div>

              <div className="pomo-inprogress-list">
                <div className="pomo-inprogress-label">进行中的任务</div>
                {inProgressTasks.length === 0
                  ? <div className="pomo-inprogress-empty">暂无进行中任务</div>
                  : inProgressTasks.map((task, idx) => (
                    <div key={task.id}
                      className={`pomo-inprogress-item${task.pending ? ' selected' : ''}${dragIdx === idx ? ' dragging' : ''}`}
                      style={task.pending ? { borderColor: task.projColor } : {}}
                      draggable
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={e => { e.preventDefault(); if (dragIdx == null || dragIdx === idx) return;
                        const newOrder = inProgressTasks.map(t => t.id);
                        const [moved] = newOrder.splice(dragIdx, 1);
                        newOrder.splice(idx, 0, moved);
                        setIpOrder(newOrder); setDragIdx(idx);
                      }}
                      onDragEnd={() => setDragIdx(null)}
                      onClick={() => { const proj = projects.find(p => p.id === task.projId); if (!proj) return; if (timerState.running) switchTask(proj, task); else togglePending(proj, task); }}
                    >
                      <span className="pomo-ip-drag">⠿</span>
                      <div className="pomo-ip-info">
                        <span className="pomo-ip-proj" style={{ color: task.projColor }}>{task.projTitle}</span>
                        <span className="pomo-ip-title">{task.title}</span>
                      </div>
                      <button className="pomo-ip-back-btn" title="退回未开始"
                        onClick={e => { e.stopPropagation(); const proj = projects.find(p => p.id === task.projId); if (proj) setStatus(proj, task, 'todo'); }}>↩</button>
                      <button className="pomo-ip-done-btn" title="标记完成"
                        onClick={e => { e.stopPropagation(); const proj = projects.find(p => p.id === task.projId); if (proj) setStatus(proj, task, 'done'); }}>✓</button>
                    </div>
                  ))
                }
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom: history | notes | stats */}
      <div className="pomo-bottom">
        <div className="pomo-history">
          <h3 className="pomo-history-title">番茄记录</h3>
          {sessions.length === 0
            ? <p className="pomo-empty">暂无记录</p>
            : (
              <div className="pomo-session-list">
                {sessions.map(s => {
                  const projs = Array.isArray(s.projects) && s.projects.length > 0
                    ? s.projects
                    : [{ title: s.projectTitle || '未分类', color: s.projectColor, minutes: s.duration }];
                  return (
                    <div key={s.id} className={`pomo-session${s.completed ? '' : ' aborted'}`}>
                      <div className="pomo-session-meta">
                        <span className="pomo-session-time">{fmtDate(s.startTime)}</span>
                        <span className="pomo-session-projs">
                          {projs.map((p, i) => (
                            <span key={i} className="pomo-session-proj-chip" style={{ color: p.color || UNCATEGORIZED_COLOR }}>
                              {p.title || '未分类'}
                              <span className="pomo-session-proj-mins">{p.minutes || 0}m</span>
                            </span>
                          ))}
                        </span>
                        <span className={`pomo-session-badge${s.completed ? '' : ' aborted'}`}>
                          {s.manual ? '补记' : s.completed ? '完成' : '中止'}
                        </span>
                      </div>
                      {s.notes && <div className="pomo-session-notes">{s.notes}</div>}
                      <div className="pomo-session-actions">
                        <button className="pomo-icon-btn pomo-edit-session" onClick={() => openEditSession(s)} title="编辑">✎</button>
                        <button className="pomo-icon-btn danger pomo-del-session" onClick={() => deleteSession(s.id)}>×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>

        <div className="pomo-notes-embed">
          <NotesEditor token={localStorage.getItem('token')} />
        </div>

        <StatsPanel sessions={sessions} projects={projects} />
      </div>
    </div>
  );
}
